import React from "react";
import { Box, Checkbox, Flex } from "flux";
import {
  PublisherPlatforms,
  EnabledPlatforms,
  FacebookPlacementSettings,
  FacebookPlacementPositions,
  InstagramPlacementPositions,
  AudienceNetworkPlacementPositions,
  MessengerPlacementPositions
} from "../../features/ad-publishing/types";
import { PlatformDisplayNames, PositionsMap } from "../../features/ad-publishing/helpers";
import { PositionCheckboxes } from "./position-checkboxes";

export type PositionsType = {
  facebook_positions: FacebookPlacementPositions[];
  instagram_positions: InstagramPlacementPositions[];
  audience_network_positions: AudienceNetworkPlacementPositions[];
  messenger_positions: MessengerPlacementPositions[];
};

const PLATFORM_CONFIG = {
  [PublisherPlatforms.FACEBOOK]: {
    positionKey: "facebook_positions" as const,
    availablePositions: FacebookPlacementPositions
  },
  [PublisherPlatforms.INSTAGRAM]: {
    positionKey: "instagram_positions" as const,
    availablePositions: InstagramPlacementPositions
  },
  [PublisherPlatforms.AUDIENCE_NETWORK]: {
    positionKey: "audience_network_positions" as const,
    availablePositions: AudienceNetworkPlacementPositions
  },
  [PublisherPlatforms.MESSENGER]: {
    positionKey: "messenger_positions" as const,
    availablePositions: MessengerPlacementPositions
  }
} as const;

interface PlatformCheckboxesProps {
  platforms: EnabledPlatforms;
  onChange: (platforms: EnabledPlatforms) => void;
  positionsData: PositionsType;
  onPositionsChange: (
    platform: keyof Omit<FacebookPlacementSettings, "platforms">,
    positions: PositionsMap[PublisherPlatforms]
  ) => void;
}

const PlatformCheckboxes: React.FC<PlatformCheckboxesProps> = ({
  platforms,
  onChange,
  positionsData,
  onPositionsChange
}) => {
  const handlePlatformChange = (platform: PublisherPlatforms) => {
    const newPlatformState = !platforms[platform];
    const config = PLATFORM_CONFIG[platform];

    onChange({
      ...platforms,
      [platform]: newPlatformState
    });

    // Get all available positions for this platform correctly
    const allPositions = newPlatformState ? Object.values(config.availablePositions) : [];
    onPositionsChange(config.positionKey, allPositions);
  };

  const handlePositionsChange = (platform: PublisherPlatforms, positions: PositionsMap[PublisherPlatforms]) => {
    const config = PLATFORM_CONFIG[platform];

    // Enable platform if any positions are selected, disable if none are selected
    const shouldPlatformBeEnabled = positions.length > 0;

    if (platforms[platform] !== shouldPlatformBeEnabled) {
      onChange({
        ...platforms,
        [platform]: shouldPlatformBeEnabled
      });
    }

    onPositionsChange(config.positionKey, positions as PositionsMap[typeof platform]);
  };

  return (
    <Box>
      <Flex flexDirection="column" gap={2}>
        {Object.values(PublisherPlatforms).map((platform) => {
          const config = PLATFORM_CONFIG[platform];

          return (
            <Box key={platform} ml={2}>
              <Flex alignItems="center" gap="8px">
                <label>
                  <Checkbox
                    size="large"
                    checked={Boolean(platforms[platform])}
                    onChange={() => handlePlatformChange(platform)}
                    dataDogIdentifier="platform names"
                  />
                  <span>{PlatformDisplayNames[platform]}</span>
                </label>
              </Flex>

              <Box ml={4} mt={1}>
                <PositionCheckboxes
                  positions={positionsData[config.positionKey]}
                  platform={platform}
                  onChange={(positions) => handlePositionsChange(platform, positions as PositionsMap[typeof platform])}
                />
              </Box>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};

export default PlatformCheckboxes;
